
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface AdminGuardProps {
  children: React.ReactNode;
}

export const AdminGuard = ({ children }: AdminGuardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let isSubscribed = true;

    const checkAdminAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          if (isSubscribed) {
            setIsLoading(false);
            navigate('/admin/auth', { replace: true });
          }
          return;
        }

        const { data: adminUser, error } = await supabase
          .from('admin_users')
          .select('email')
          .eq('email', session.user.email)
          .single();

        if (error || !adminUser) {
          if (isSubscribed) {
            await supabase.auth.signOut();
            toast({
              title: "Acceso Denegado",
              description: "No tienes privilegios de administrador",
              variant: "destructive",
            });
            navigate('/admin/auth', { replace: true });
          }
        } else if (isSubscribed) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Admin check error:', error);
        if (isSubscribed) {
          navigate('/admin/auth', { replace: true });
        }
      } finally {
        if (isSubscribed) {
          setIsLoading(false);
        }
      }
    };

    checkAdminAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' && isSubscribed) {
        setIsAdmin(false);
        navigate('/admin/auth', { replace: true });
      }
    });

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, [navigate, toast]);

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return isAdmin ? <>{children}</> : null;
};
